<template>
  <div class="live-monitor-wrapper" :class="layoutClass">
    <div class="live-monitor" :class="monitorClass">
      <div class="monitor-content">
        <div class="monitor-status">
          <b-spinner v-if="isRefreshing" small class="mr-2" variant="primary" />
          <span v-else class="status-indicator" :class="{ live: isEnabled, paused: !isEnabled }"></span>
          
          <span class="status-text">
            <template v-if="isRefreshing">
              Обновление данных...
            </template>
            <template v-else-if="isEnabled">
              <strong>Онлайн</strong> · Автообновление каждые {{ interval }}с
              <span v-if="countdown > 0" class="countdown">· {{ countdown }}с</span>
            </template>
            <template v-else>
              Автообновление приостановлено
            </template>
          </span>
        </div>
        
        <div class="monitor-actions">
          <b-input-group
            v-b-tooltip.hover.bottom="'Интервал между автоматическими обновлениями данных с внешнего источника'"
            size="sm"
            class="interval-input-group"
          >
            <b-form-input
              v-model.number="intervalDraft"
              type="number"
              :min="minRefreshIntervalSec"
              :max="maxRefreshIntervalSec"
              :disabled="isAnalyzing || isRefreshing"
              class="interval-input"
              @blur="commitIntervalDraft"
              @keyup.enter="commitIntervalDraft"
            />
            <b-input-group-append is-text>
              с
            </b-input-group-append>
          </b-input-group>
          <b-button 
            v-b-tooltip.hover.bottom="getStartTooltip()"
            size="sm" 
            :variant="isEnabled ? 'flat-warning' : 'flat-success'"
            :disabled="isAnalyzing || isRefreshing || isSavingConfig"
            @click="$emit('toggle')"
          >
            {{ isEnabled ? 'Пауза' : 'Старт' }}
          </b-button>
          <b-button 
            v-b-tooltip.hover.bottom="'Немедленное обновление данных с внешнего источника'"
            size="sm" 
            variant="outline-primary"
            :disabled="isRefreshing || isAnalyzing || isSavingConfig"
            @click="$emit('refresh-now')"
          >
            <b-spinner v-if="isRefreshing" small class="mr-1" />
            <template v-else>Обновить</template>
          </b-button>
          <b-button 
            v-if="hasUnsavedConfig"
            v-b-tooltip.hover.bottom="'Отменить локальные изменения конфигурации и вернуть последнее сохраненное состояние'"
            size="sm"
            variant="outline-secondary"
            :disabled="isSavingConfig || isAnalyzing || isRefreshing"
            @click="$emit('discard-config')"
          >
            Сбросить конфигурацию
          </b-button>
          <b-button
            v-if="hasUnsavedConfig"
            v-b-tooltip.hover.bottom="'Сохранить конфигурацию (чекпоинты и участники) на сервер'"
            size="sm" 
            variant="warning"
            :disabled="isSavingConfig || isAnalyzing || isRefreshing"
            @click="$emit('save-config')"
          >
            <b-spinner v-if="isSavingConfig" small class="mr-1" />
            <b-icon v-else icon="check2" class="mr-1" />
            Сохранить конфигурацию
          </b-button>
        </div>
      </div>

      <!-- Blocking operation (reprocess / manual corrections) -->
      <div
        v-if="blockingLabel"
        class="blocking-banner"
        role="status"
      >
        <b-spinner
          small
          variant="primary"
          class="mr-2"
        />
        <span>{{ blockingLabel }}</span>
      </div>

      <!-- Pending changes section -->
      <div v-if="hasPendingChanges" class="pending-changes-section">
        <div class="changes-indicator">
          <b-icon icon="exclamation-circle" class="mr-2" />
          <span>
            <strong>{{ pendingChangesCount }}</strong> 
            {{ pendingChangesCount === 1 ? 'изменение' : (pendingChangesCount < 5 ? 'изменения' : 'изменений') }}
          </span>
        </div>
        
        <div class="changes-actions">
          <b-button
            v-b-tooltip.hover.bottom="'Отменить все несохраненные изменения'"
            size="sm"
            variant="flat-secondary"
            :disabled="isApplyingChanges || isAnalyzing"
            @click="$emit('discard-changes')"
          >
            <b-icon icon="x" class="mr-1" />
            Отменить все
          </b-button>
          <b-button
            v-b-tooltip.hover.bottom="'Применить все изменения и пересчитать результаты'"
            size="sm"
            variant="success"
            :disabled="isApplyingChanges || isAnalyzing || isRefreshing || isSavingConfig"
            @click="$emit('apply-changes')"
          >
            <b-spinner v-if="isApplyingChanges" small class="mr-1" />
            <b-icon v-else icon="check" class="mr-1" />
            Применить изменения
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BSpinner, BIcon, BFormInput, BInputGroup, BInputGroupAppend, VBTooltip,
} from 'bootstrap-vue'
import {
  MIN_REFRESH_INTERVAL_SEC,
  MAX_REFRESH_INTERVAL_SEC,
  normalizeRefreshIntervalSec,
} from '../../utils/refresh-interval-storage'

export default {
  name: 'LiveMonitor',
  components: {
    BButton,
    BSpinner,
    BIcon,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    isEnabled: {
      type: Boolean,
      default: false,
    },
    interval: {
      type: Number,
      default: 30,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    countdown: {
      type: Number,
      default: 0,
    },
    pendingChanges: {
      type: Array,
      default: function() {
        return []
      },
    },
    hasUnsavedConfig: {
      type: Boolean,
      default: false,
    },
    isSavingConfig: {
      type: Boolean,
      default: false,
    },
    isApplyingChanges: {
      type: Boolean,
      default: false,
    },
    isAnalyzing: {
      type: Boolean,
      default: false,
    },
    blockingLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      layoutClass: 'light-layout',
      themeObserver: null,
      intervalDraft: this.interval,
      minRefreshIntervalSec: MIN_REFRESH_INTERVAL_SEC,
      maxRefreshIntervalSec: MAX_REFRESH_INTERVAL_SEC,
    }
  },
  watch: {
    interval(nextValue) {
      this.intervalDraft = nextValue
    },
  },
  computed: {
    monitorClass() {
      return {
        'is-live': this.isEnabled,
        'is-paused': !this.isEnabled,
        'is-refreshing': this.isRefreshing,
        'has-pending-changes': this.hasPendingChanges,
      }
    },

    hasPendingChanges() {
      return this.pendingChanges && this.pendingChanges.length > 0
    },

    pendingChangesCount() {
      return this.pendingChanges ? this.pendingChanges.length : 0
    },
  },
  mounted() {
    this.setupThemeObserver()
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  },
  methods: {
    detectTheme() {
      const html = document.documentElement
      const body = document.body
      
      if (html.classList.contains('dark-layout') || 
          body.classList.contains('dark-layout') ||
          html.classList.contains('theme-dark')) {
        this.layoutClass = 'dark-layout'
      } else {
        this.layoutClass = 'light-layout'
      }
    },

    setupThemeObserver() {
      this.detectTheme()
      
      const observerConfig = { attributes: true, attributeFilter: ['class'] }
      this.themeObserver = new MutationObserver(() => {
        this.detectTheme()
      })
      
      this.themeObserver.observe(document.documentElement, observerConfig)
      this.themeObserver.observe(document.body, observerConfig)
    },

    getStartTooltip() {
      if (this.isEnabled) {
        return 'Остановить автоматическое обновление данных. Можно вносить корректировки без постоянного пересчета.'
      }
      return `Запустить автообновление данных с периодическим обновлением каждые ${this.interval} секунд.`
    },

    commitIntervalDraft() {
      const normalized = normalizeRefreshIntervalSec(this.intervalDraft)
      if (normalized == null) {
        this.intervalDraft = this.interval
        return
      }
      this.intervalDraft = normalized
      if (normalized !== this.interval) {
        this.$emit('interval-change', normalized)
      }
    },
  },
}
</script>

<style scoped>
/* Sticky positioning lives on parent `.race-analyzer-v2__sticky-toolbar` in index.vue */
.live-monitor-wrapper {
  margin-bottom: 1.5rem;
  background: transparent;
}

/* Dark theme */
.live-monitor-wrapper.dark-layout .live-monitor {
  background: linear-gradient(118deg, #1e2139, #161d31);
  border: 1px solid #3b4253;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.25);
}

.live-monitor-wrapper.dark-layout .monitor-status {
  color: #d0d2d6;
}

.live-monitor-wrapper.dark-layout .status-text strong {
  color: #9d93f6;
}

.live-monitor-wrapper.dark-layout .countdown {
  color: #a8aaae;
}

.live-monitor-wrapper.dark-layout .pending-changes-section {
  border-top: 1px solid #3b4253;
}

.live-monitor-wrapper.dark-layout .changes-indicator {
  color: #ff9f43;
}

.live-monitor-wrapper.dark-layout .changes-indicator strong {
  color: #ff9f43;
}

/* Light theme */
.live-monitor-wrapper.light-layout .live-monitor {
  background: #fff;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
}

.live-monitor-wrapper.light-layout .monitor-status {
  color: #5e5873;
}

.live-monitor-wrapper.light-layout .status-text strong {
  color: #7367f0;
}

.live-monitor-wrapper.light-layout .countdown {
  color: #b9b9c3;
}

.live-monitor-wrapper.light-layout .pending-changes-section {
  border-top: 1px solid #ebe9f1;
}

.live-monitor-wrapper.light-layout .changes-indicator {
  color: #ff9f43;
}

.live-monitor-wrapper.light-layout .changes-indicator strong {
  color: #ff9f43;
}

/* Common styles */
.live-monitor {
  padding: 1rem 1.5rem;
  border-radius: 0.428rem;
  transition: all 0.3s ease;
}

/* Enhanced shadow when scrolled */
.live-monitor-wrapper:not(:first-child) .live-monitor,
.live-monitor-wrapper.scrolled .live-monitor {
  box-shadow: 0 6px 30px 0 rgba(34, 41, 47, 0.15);
}

.live-monitor.is-live {
  border-left: 3px solid #7367f0;
}

.live-monitor.is-paused {
  border-left: 3px solid #82868b;
}

.live-monitor.has-pending-changes {
  border-left: 3px solid #ff9f43;
}

.live-monitor.is-refreshing {
  position: relative;
  overflow: hidden;
}

.live-monitor.is-refreshing::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #7367f0, transparent);
  animation: loading-slide 1.5s ease-in-out infinite;
}

@keyframes loading-slide {
  0% { left: -100%; }
  100% { left: 100%; }
}

.monitor-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.monitor-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-indicator {
  font-size: 0.75rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.live {
  background: #7367f0;
  box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.7);
  animation: pulse-dot 2s infinite;
}

.status-indicator.paused {
  background: #82868b;
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(115, 103, 240, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0);
  }
}

.status-text {
  font-size: 0.95rem;
  font-weight: 500;
}

.status-text strong {
  color: #9d93f6;
  font-weight: 600;
}

.countdown {
  color: #a8aaae;
  font-size: 0.857rem;
  font-weight: 400;
}

.monitor-actions {
  display: flex;
  gap: 0.571rem;
  align-items: center;
}

.interval-input-group {
  width: 7.5rem;
}

.interval-input {
  text-align: right;
}

.blocking-banner {
  margin-top: 0.85rem;
  padding: 0.65rem 0.85rem;
  border-radius: 0.358rem;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(115, 103, 240, 0.35);
  background: rgba(115, 103, 240, 0.08);
  color: #5e5873;
}

.live-monitor-wrapper.dark-layout .blocking-banner {
  color: #d0d2d6;
  border-color: rgba(157, 147, 246, 0.45);
  background: rgba(115, 103, 240, 0.12);
}

/* Pending changes section */
.pending-changes-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #3b4253;
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: slide-in 0.3s ease-out;
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.changes-indicator {
  display: flex;
  align-items: center;
  color: #ff9f43;
  font-size: 0.95rem;
  font-weight: 500;
}

.changes-indicator strong {
  color: #ff9f43;
  font-weight: 600;
}

.changes-actions {
  display: flex;
  gap: 0.571rem;
}
</style>

